import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { inquiryApi, Inquiry, InquiryListParams } from '../../../services/admin/inquiryApi';
import { createReply, updateReply, deleteReply } from '../../../services/postApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import RichTextEditor from '../../common/RichTextEditor';
import {
  CloseButton,
  ErrorMessage,
  ModalCard,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  PrimaryButton,
  SecondaryButton,
  SidePanelBody,
  SidePanelHeader,
  SidePanelModal,
} from '../common/AdminModalPrimitives';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  HeaderCell,
  HeaderText,
  RetryButton,
  Spinner,
  StateText,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
  TableState,
} from '../common/AdminTablePrimitives';
import CommunityTablePagination from '../community/CommunityTablePagination';

const InquiryListComponent: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReplyId, setDeleteReplyId] = useState<number | null>(null);

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: InquiryListParams = {
        page: currentPage - 1,
        size: 10,
      };

      const response = await inquiryApi.getInquiries(params);
      setInquiries(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch {
      setError('문의 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleInquiryClick = async (inquiry: Inquiry) => {
    try {
      const inquiryDetails = await inquiryApi.getInquiryById(inquiry.id);
      setSelectedInquiry(inquiryDetails);
      setShowInquiryModal(true);
    } catch {
      setError('문의 상세 정보를 불러오지 못했습니다.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteReplyId) {
      return;
    }

    try {
      await deleteReply(deleteReplyId);
      setShowDeleteModal(false);
      setDeleteReplyId(null);
      await fetchInquiries();
      alert('답변을 삭제했습니다.');
    } catch {
      setError('답변 삭제에 실패했습니다.');
    }
  };

  const isInitialLoading = loading && inquiries.length === 0;
  if (isInitialLoading) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>받은 문의</AdminPageTitle>
        </AdminPageHeader>
        <AdminSection>
          <TableState>
            <Spinner />
            <StateText>로딩 중입니다.</StateText>
          </TableState>
        </AdminSection>
      </AdminPage>
    );
  }

  if (error && inquiries.length === 0) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>받은 문의</AdminPageTitle>
        </AdminPageHeader>
        <AdminSection>
          <ErrorState>
            <ErrorMessage>{error}</ErrorMessage>
            <RetryButton onClick={fetchInquiries}>다시 시도</RetryButton>
          </ErrorState>
        </AdminSection>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>문의 관리</AdminPageTitle>
      </AdminPageHeader>

      <AdminSection>
        <TableContainer>
          <TableInner>
            <TableHeader>
              <TableHeaderRow $columns="60px minmax(200px, 4fr) 100px minmax(70px, 1.4fr) 110px 110px">
                <HeaderCell><HeaderText>연번</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>제목</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>상태</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>문의자</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>문의일</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
              </TableHeaderRow>
            </TableHeader>

            <TableBody>
              {inquiries.map((inquiry, index) => (
                <ClickableRow key={inquiry.id} $columns="60px minmax(200px, 4fr) 100px minmax(70px, 1.4fr) 110px 110px" onClick={() => void handleInquiryClick(inquiry)}>
                  <BodyCell>
                    <CellText>{totalElements - (currentPage - 1) * 10 - index}</CellText>
                  </BodyCell>
                  <BodyCell>
                    <TitleText title={inquiry.title}>{inquiry.title}</TitleText>
                  </BodyCell>
                  <BodyCell>
                    <StatusText $answered={inquiry.hasReply}>{inquiry.hasReply ? '답변 완료' : '답변 전'}</StatusText>
                  </BodyCell>
                  <BodyCell>
                    <CellText>{inquiry.author}</CellText>
                  </BodyCell>
                  <BodyCell>
                    <CellText>{new Date(inquiry.createdAt).toLocaleDateString('ko-KR')}</CellText>
                  </BodyCell>
                  <BodyCell>
                    <ActionButtons>
                      <ActionButton
                        type="button"
                        $tone="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleInquiryClick(inquiry);
                        }}
                      >
                        {inquiry.hasReply ? '수정' : '답변'}
                      </ActionButton>
                      {inquiry.hasReply && inquiry.reply && (
                        <ActionButton
                          type="button"
                          $tone="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteReplyId(inquiry.reply!.reply_id);
                            setShowDeleteModal(true);
                          }}
                        >
                          삭제
                        </ActionButton>
                      )}
                    </ActionButtons>
                  </BodyCell>
                </ClickableRow>
              ))}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </TableContainer>
      </AdminSection>

      {showInquiryModal && selectedInquiry && (
        <InquiryReplyModal
          inquiry={selectedInquiry}
          onClose={() => {
            setShowInquiryModal(false);
            setSelectedInquiry(null);
          }}
          onUpdate={fetchInquiries}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteReplyId(null);
          }}
          message="답변을 삭제하시겠습니까?"
        />
      )}
    </AdminPage>
  );
};

interface InquiryReplyModalProps {
  inquiry: Inquiry;
  onClose: () => void;
  onUpdate: () => void;
}

export const InquiryReplyModal: React.FC<InquiryReplyModalProps> = ({ inquiry, onClose, onUpdate }) => {
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (inquiry.hasReply && inquiry.reply) {
      setReplyContent(inquiry.reply.content || '');
    } else {
      setReplyContent('');
    }
  }, [inquiry]);

  const renderContent = (content: string) => {
    const codeBlockRegex = /```[\w]*\n?([\s\S]*?)```/g;
    const htmlContent = content.replace(codeBlockRegex, (_match, code) => {
      const lines = code
        .replace(/\r?\n$/, '')
        .split(/\r?\n/)
        .map(
          (line: string) =>
            `<span class="tbc-line">${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`,
        )
        .join('\n');
      const highlighted = lines
        .replace(/(&lt;\/?)([a-zA-Z0-9_-]+)(?=[\s&gt;])/g, '$1<span class="tbc-token-tag">$2</span>')
        .replace(/([a-zA-Z0-9_-]+)=(&quot;.*?&quot;|'.*?')/g, '<span class="tbc-token-attr">$1</span>=<span class="tbc-token-string">$2</span>')
        .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="tbc-token-comment">$1</span>');
      return `<pre><code>${highlighted}</code></pre>`;
    });

    return htmlContent.replace(/`([^`]+)`/g, '<code>$1</code>').replace(/\n/g, '<br>');
  };

  const handleReplySubmit = async () => {
    if (!replyContent.trim()) {
      alert('답변 내용을 입력해 주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      const htmlContent = renderContent(replyContent);
      const replyResponse =
        inquiry.hasReply && inquiry.reply
          ? await updateReply(inquiry.reply.reply_id, htmlContent, inquiry.id)
          : await createReply({ postId: inquiry.id, htmlContent });

      if (replyResponse.code === 200 || replyResponse.code === 201) {
        alert(inquiry.hasReply ? '답변을 수정했습니다.' : '답변을 등록했습니다.');
        onUpdate();
        onClose();
      } else {
        throw new Error();
      }
    } catch {
      alert('답변 처리에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()} style={{ justifyContent: 'flex-end', padding: 0 }}>
      <SidePanelModal>
        <SidePanelHeader>
          <ModalTitle>답변 등록</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="닫기">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="#171719" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </CloseButton>
        </SidePanelHeader>

        <SidePanelBody>
          <InfoSection>
            <InfoBlock>
              <InfoLabel>작성자</InfoLabel>
              <InfoValue>{inquiry.author}</InfoValue>
            </InfoBlock>
            <InfoBlock>
              <InfoLabel>작성일</InfoLabel>
              <InfoValue>{new Date(inquiry.createdAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '')}</InfoValue>
            </InfoBlock>
          </InfoSection>

          <InfoBlock>
            <InfoLabel>제목</InfoLabel>
            <InfoValue>{inquiry.title}</InfoValue>
          </InfoBlock>

          <InfoBlock>
            <InfoLabel>내용</InfoLabel>
            <ContentView dangerouslySetInnerHTML={{ __html: inquiry.content }} />
          </InfoBlock>

          <InfoBlock>
            <InfoLabel>답변</InfoLabel>
            <EditorFrame>
              <RichTextEditor
                value={replyContent}
                onChange={setReplyContent}
                placeholder="내용을 입력해 주세요."
                minHeight="260px"
                attachmentsEditable
              />
            </EditorFrame>
          </InfoBlock>
        </SidePanelBody>

        <ReplyFooter>
          <PrimaryButton type="button" onClick={handleReplySubmit} disabled={!replyContent.trim() || isSubmitting}>
            {isSubmitting ? '등록 중...' : '등록하기'}
          </PrimaryButton>
        </ReplyFooter>
      </SidePanelModal>
    </ModalOverlay>
  );
};

interface DeleteConfirmModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  message: string;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ onConfirm, onCancel, message }) => (
  <ModalOverlay onClick={onCancel}>
    <ModalCard $maxWidth="400px" onClick={(e) => e.stopPropagation()}>
      <ConfirmWrap>
        <ConfirmIcon>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path
              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              stroke="#448181"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M12 9V13" stroke="#448181" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 17H12.01" stroke="#448181" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ConfirmIcon>
        <ConfirmTitle>{message}</ConfirmTitle>
        <ConfirmText>삭제된 답변은 복구할 수 없습니다.</ConfirmText>
      </ConfirmWrap>
      <ModalFooter>
        <SecondaryButton type="button" onClick={onCancel}>
          취소
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onConfirm}>
          삭제
        </PrimaryButton>
      </ModalFooter>
    </ModalCard>
  </ModalOverlay>
);

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ClickableRow = styled(TableRow)`
  cursor: pointer;
`;

const TitleText = styled(CellText)`
  display: block;
  width: 100%;
`;

const StatusText = styled.span<{ $answered: boolean }>`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 150%;
  white-space: nowrap;
  color: ${({ $answered }) => ($answered ? '#296768' : 'rgba(55, 56, 60, 0.61)')};

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(55, 56, 60, 0.61);
`;

const InfoValue = styled.div`
  padding: 12px;
  border-radius: 12px;
  background: #f4f4f5;
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
`;

const ContentView = styled.div`
  padding: 12px;
  border-radius: 12px;
  background: #f4f4f5;
  color: rgba(55, 56, 60, 0.7);
  font-size: 16px;
  line-height: 1.625;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 8px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EditorFrame = styled.div`
  border-radius: 12px;
  overflow: hidden;
`;

const ReplyFooter = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
`;

const ConfirmWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px 32px 0;
`;

const ConfirmIcon = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConfirmTitle = styled.div`
  color: #171719;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const ConfirmText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`;

export default InquiryListComponent;
